import * as THREE from "three";
import {
  natureReady,
  natureTemplates,
  pickNatureTemplate,
  type NatureFamily,
  type NatureTemplate,
} from "./NatureAssets";
import { mat, sharedSphere, sharedCylinder, WORLD_PROP_SCALE } from "./WorldModels";
import { contactShadowGeometry, contactShadowMaterial } from "./ContactShadow";

export interface VegPose {
  x: number;
  y: number;
  z: number;
  rotY: number;
  scale: number;
  variant?: number;
  /** Prefer Mini Forest pines when available (horizon). */
  family?: "tree" | "pine";
  /** Warm fall canopy tint (deciduous only). */
  autumn?: boolean;
}

/**
 * Batches trees / bushes / flowers / rocks into InstancedMeshes.
 * Prefers Kenney Nature + Mini Forest GLBs (recolored to Dogdell palette);
 * falls back to cozy procedural primitives if assets are not loaded.
 */
export class InstancedVegetation {
  readonly root = new THREE.Group();
  private readonly trees: VegPose[] = [];
  private readonly bushes: VegPose[] = [];
  private readonly flowers: VegPose[] = [];
  private readonly rocks: VegPose[] = [];
  private readonly plants: VegPose[] = [];
  private built = false;
  /** Kenney flower / grass / bush instances that get a soft wind tip each frame. */
  private readonly flowerSway: VegSwayBatch[] = [];
  private readonly plantSway: VegSwayBatch[] = [];
  private readonly bushSway: VegSwayBatch[] = [];
  /** Procedural bloom layers (fallback when Kenney flowers are missing). */
  private proceduralFlowerLayers: THREE.InstancedMesh[] | null = null;
  private readonly swayDummy = new THREE.Object3D();
  private readonly swayLocal = new THREE.Object3D();
  private readonly swayScratch = new THREE.Matrix4();

  addTree(pose: VegPose): void {
    this.trees.push(pose);
  }

  addBush(pose: VegPose): void {
    this.bushes.push(pose);
  }

  addFlower(pose: VegPose): void {
    this.flowers.push(pose);
  }

  addRock(pose: VegPose): void {
    this.rocks.push(pose);
  }

  addPlant(pose: VegPose): void {
    this.plants.push(pose);
  }

  build(): void {
    if (this.built) {
      return;
    }
    this.built = true;
    if (this.trees.length > 0) {
      this.buildFromFamily(this.trees, "tree", () => this.buildProceduralTrees());
      addGroundShadows(this.root, this.trees, 0.95, 0.62, 0.42);
    }
    if (this.bushes.length > 0) {
      this.buildFromFamily(this.bushes, "bush", () => this.buildProceduralBushes(), this.bushSway);
      addGroundShadows(this.root, this.bushes, 0.48, 0.34, 0.3);
    }
    if (this.flowers.length > 0) {
      this.buildFromFamily(this.flowers, "flower", () => this.buildProceduralFlowers(), this.flowerSway);
    }
    if (this.rocks.length > 0) {
      this.buildFromFamily(this.rocks, "rock", () => this.buildProceduralRocks());
    }
    if (this.plants.length > 0) {
      this.buildFromFamily(this.plants, "plant", () => {
        /* optional ground plants - skip if missing */
      }, this.plantSway);
      addGroundShadows(this.root, this.plants, 0.28, 0.2, 0.22);
    }
  }

  /** Traveling park breeze — gust fronts cascade through flowers / grass / bushes. */
  update(timeMs: number): void {
    if (
      this.flowerSway.length === 0 &&
      this.plantSway.length === 0 &&
      this.bushSway.length === 0 &&
      !this.proceduralFlowerLayers
    ) {
      return;
    }
    const t = timeMs * 0.001;
    const dummy = this.swayDummy;
    const scratch = this.swayScratch;

    const writeBatch = (batches: VegSwayBatch[], amp: number) => {
      for (const batch of batches) {
        for (let i = 0; i < batch.poses.length; i++) {
          const pose = batch.poses[i]!;
          applyVegSway(dummy, pose, t, amp);
          dummy.updateMatrix();
          scratch.multiplyMatrices(dummy.matrix, batch.partMatrix);
          batch.mesh.setMatrixAt(i, scratch);
        }
        batch.mesh.instanceMatrix.needsUpdate = true;
      }
    };
    writeBatch(this.flowerSway, 0.135);
    writeBatch(this.plantSway, 0.158);
    writeBatch(this.bushSway, 0.048);

    if (this.proceduralFlowerLayers) {
      const local = this.swayLocal;
      const offsets = [
        [0, 0.16, 0],
        [0.14, 0.15, 0.06],
        [-0.12, 0.14, -0.08],
      ] as const;
      for (let c = 0; c < this.proceduralFlowerLayers.length; c++) {
        const layer = this.proceduralFlowerLayers[c]!;
        const [ox, oy, oz] = offsets[c % offsets.length]!;
        for (let i = 0; i < this.flowers.length; i++) {
          const pose = this.flowers[i]!;
          applyVegSway(dummy, pose, t, 0.135);
          dummy.updateMatrix();
          local.position.set(ox, oy, oz);
          local.rotation.set(0, 0, 0);
          local.scale.set(1, 1, 1);
          local.updateMatrix();
          scratch.copy(dummy.matrix).multiply(local.matrix);
          layer.setMatrixAt(i, scratch);
        }
        layer.instanceMatrix.needsUpdate = true;
      }
    }
  }

  destroy(): void {
    this.root.clear();
    this.root.removeFromParent();
    this.trees.length = 0;
    this.bushes.length = 0;
    this.flowers.length = 0;
    this.rocks.length = 0;
    this.plants.length = 0;
    this.flowerSway.length = 0;
    this.plantSway.length = 0;
    this.bushSway.length = 0;
    this.proceduralFlowerLayers = null;
    this.built = false;
  }

  private buildFromFamily(
    poses: VegPose[],
    family: NatureFamily,
    fallback: () => void,
    trackSway?: VegSwayBatch[],
  ): void {
    if (!natureReady()) {
      fallback();
      return;
    }
    // Trees may mix deciduous + pine by pose.family.
    if (family === "tree") {
      const byKey = new Map<string, { template: NatureTemplate; poses: VegPose[] }>();
      for (const pose of poses) {
        const want: NatureFamily =
          pose.family === "pine" && natureTemplates("pine").length > 0 ? "pine" : "tree";
        const list = natureTemplates(want);
        if (list.length === 0) {
          continue;
        }
        const template = pickNatureTemplate(want, pose.variant ?? 0)!;
        const bucket = byKey.get(template.id) ?? { template, poses: [] };
        bucket.poses.push(pose);
        byKey.set(template.id, bucket);
      }
      if (byKey.size === 0) {
        fallback();
        return;
      }
      for (const { template, poses: group } of byKey.values()) {
        instanceTemplate(this.root, template, group);
      }
      return;
    }

    const list = natureTemplates(family);
    if (list.length === 0) {
      fallback();
      return;
    }
    const byKey = new Map<string, { template: NatureTemplate; poses: VegPose[] }>();
    for (const pose of poses) {
      const template = pickNatureTemplate(family, pose.variant ?? 0)!;
      const bucket = byKey.get(template.id) ?? { template, poses: [] };
      bucket.poses.push(pose);
      byKey.set(template.id, bucket);
    }
    for (const { template, poses: group } of byKey.values()) {
      instanceTemplate(this.root, template, group, trackSway);
    }
  }

  private buildProceduralTrees(): void {
    const n = this.trees.length;
    const trunk = new THREE.InstancedMesh(sharedCylinder(0.09, 0.13, 1.05, 6), mat(0x8d6e63).clone(), n);
    const canopyA = new THREE.InstancedMesh(sharedSphere(0.55, 7, 5), mat(0x86c858).clone(), n);
    const canopyB = new THREE.InstancedMesh(sharedSphere(0.36, 6, 5), mat(0x6eb048).clone(), n);
    trunk.frustumCulled = true;
    canopyA.frustumCulled = true;
    canopyB.frustumCulled = true;

    const dummy = new THREE.Object3D();
    const local = new THREE.Object3D();
    const tint = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const t = this.trees[i]!;
      const alt = (t.variant ?? 0) === 1;
      writeInstance(dummy, local, trunk, i, t, 0, alt ? 0.42 : 0.52, 0, 1, alt ? 0.9 : 1, 1);
      writeInstance(
        dummy,
        local,
        canopyA,
        i,
        t,
        0,
        alt ? 1.35 : 1.55,
        0,
        alt ? 1.05 : 1.15,
        0.95,
        alt ? 1.05 : 1.15,
      );
      writeInstance(dummy, local, canopyB, i, t, alt ? 0.08 : 0.12, alt ? 1.85 : 2.1, 0.05, 1.05, 1.05, 1.05);
      jitterInstanceColor(trunk, i, t, "trunk", tint);
      jitterInstanceColor(canopyA, i, t, "leaf", tint);
      jitterInstanceColor(canopyB, i, t, "leaf", tint);
    }
    trunk.instanceMatrix.needsUpdate = true;
    canopyA.instanceMatrix.needsUpdate = true;
    canopyB.instanceMatrix.needsUpdate = true;
    if (trunk.instanceColor) trunk.instanceColor.needsUpdate = true;
    if (canopyA.instanceColor) canopyA.instanceColor.needsUpdate = true;
    if (canopyB.instanceColor) canopyB.instanceColor.needsUpdate = true;
    this.root.add(trunk, canopyA, canopyB);
  }

  private buildProceduralBushes(): void {
    const n = this.bushes.length;
    const body = new THREE.InstancedMesh(sharedSphere(0.28, 6, 5), mat(0x86c858).clone(), n);
    const puff = new THREE.InstancedMesh(sharedSphere(0.18, 5, 4), mat(0xa0d468).clone(), n);
    const berry = new THREE.InstancedMesh(sharedSphere(0.05, 5, 4), mat(0xf48fb1), n);
    const dummy = new THREE.Object3D();
    const local = new THREE.Object3D();
    const tint = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const b = this.bushes[i]!;
      writeInstance(dummy, local, body, i, b, 0, 0.24, 0, 1.15, 0.9, 1.05);
      writeInstance(dummy, local, puff, i, b, 0.16, 0.28, 0.08, 1, 1, 1);
      writeInstance(dummy, local, berry, i, b, 0.08, 0.38, 0.1, 1, 1, 1);
      jitterInstanceColor(body, i, b, "leaf", tint);
      jitterInstanceColor(puff, i, b, "leaf", tint);
    }
    body.instanceMatrix.needsUpdate = true;
    puff.instanceMatrix.needsUpdate = true;
    berry.instanceMatrix.needsUpdate = true;
    if (body.instanceColor) body.instanceColor.needsUpdate = true;
    if (puff.instanceColor) puff.instanceColor.needsUpdate = true;
    this.root.add(body, puff, berry);
  }

  private buildProceduralFlowers(): void {
    const n = this.flowers.length;
    const colors = [0xf8bbd0, 0xffe082, 0xe1bee7, 0xffccbc];
    const layers = colors.map(
      (color) => new THREE.InstancedMesh(sharedSphere(0.07, 5, 4), mat(color), n),
    );
    const dummy = new THREE.Object3D();
    const local = new THREE.Object3D();
    const offsets = [
      [0, 0.16, 0],
      [0.14, 0.15, 0.06],
      [-0.12, 0.14, -0.08],
    ] as const;
    for (let i = 0; i < n; i++) {
      const f = this.flowers[i]!;
      for (let c = 0; c < layers.length; c++) {
        const [ox, oy, oz] = offsets[c % offsets.length]!;
        writeInstance(dummy, local, layers[c]!, i, f, ox, oy, oz, 1, 1, 1);
      }
    }
    for (const layer of layers) {
      layer.instanceMatrix.needsUpdate = true;
      this.root.add(layer);
    }
    this.proceduralFlowerLayers = layers;
  }

  private buildProceduralRocks(): void {
    const n = this.rocks.length;
    const main = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.34, 0), mat(0x9e958a), n);
    const chip = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.14, 0), mat(0xb8b0a4), n);
    const dummy = new THREE.Object3D();
    const local = new THREE.Object3D();
    for (let i = 0; i < n; i++) {
      const r = this.rocks[i]!;
      writeInstance(dummy, local, main, i, r, 0, 0.24, 0, 1.2, 0.75, 1);
      writeInstance(dummy, local, chip, i, r, 0.22, 0.12, 0.08, 1, 0.7, 0.9);
    }
    main.instanceMatrix.needsUpdate = true;
    chip.instanceMatrix.needsUpdate = true;
    this.root.add(main, chip);
  }
}

function instanceTemplate(
  root: THREE.Group,
  template: NatureTemplate,
  poses: VegPose[],
  trackSway?: VegSwayBatch[],
): void {
  const n = poses.length;
  const dummy = new THREE.Object3D();
  const scratch = new THREE.Matrix4();
  const tint = new THREE.Color();
  for (const part of template.parts) {
    // Clone so instance colors don't fight shared materials used elsewhere.
    const material = part.material.clone();
    material.userData.shared = false;
    const mesh = new THREE.InstancedMesh(part.geometry, material, n);
    mesh.frustumCulled = true;
    const role = colorRole(part.material.color.getHex());
    for (let i = 0; i < n; i++) {
      const pose = poses[i]!;
      dummy.position.set(pose.x, pose.y, pose.z);
      dummy.rotation.set(0, pose.rotY, 0);
      dummy.scale.setScalar(pose.scale);
      dummy.updateMatrix();
      scratch.multiplyMatrices(dummy.matrix, part.matrix);
      mesh.setMatrixAt(i, scratch);
      if (role !== "other") {
        jitterInstanceColor(mesh, i, pose, role, tint);
      }
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true;
    }
    root.add(mesh);
    if (trackSway) {
      trackSway.push({ mesh, partMatrix: part.matrix.clone(), poses });
    }
  }
}

interface VegSwayBatch {
  mesh: THREE.InstancedMesh;
  partMatrix: THREE.Matrix4;
  poses: VegPose[];
}

/** Wind blows roughly SW→NE so gusts read as a front crossing the park. */
export const PARK_WIND_DIR_X = 0.82;
export const PARK_WIND_DIR_Z = 0.57;

export interface ParkWindSample {
  /** Main lean with the breeze (− recovery, + pushed downwind). */
  along: number;
  /** Side flutter perpendicular to the wind. */
  cross: number;
}

/** Soften peaks; recover slower upwind so stems don't pendulum. */
function softGust(v: number): number {
  const c = Math.tanh(v * 1.05);
  return c >= 0 ? c : c * 0.55;
}

/**
 * Shared traveling breeze: a soft front sweeps the park, with layered noise
 * and cross-flutter so beds cascade without looking metronomic.
 */
export function parkWindSample(x: number, z: number, tSec: number): ParkWindSample {
  const alongPos = x * PARK_WIND_DIR_X + z * PARK_WIND_DIR_Z;
  const acrossPos = -x * PARK_WIND_DIR_Z + z * PARK_WIND_DIR_X;

  // Traveling gust + slower swell (different wavelengths so crests don't line up).
  const gustPhase = alongPos * 0.31 - tSec * 1.45;
  const swellPhase = alongPos * 0.12 - tSec * 0.52;
  const eddyPhase = acrossPos * 0.55 + alongPos * 0.08 - tSec * 0.9;

  const gust =
    Math.sin(gustPhase) * 0.5 +
    Math.sin(gustPhase * 1.63 + 0.9) * 0.27 +
    Math.sin(gustPhase * 0.37 + acrossPos * 0.15) * 0.18;
  const swell = Math.sin(swellPhase + 0.4) * 0.62 + Math.sin(swellPhase * 2.05 + 1.1) * 0.28;
  const eddy = Math.sin(eddyPhase) * 0.22;

  // Tiny plant-to-plant variance (phase only) — keeps the cascade readable.
  const plant = Math.sin(x * 2.7 + z * 4.9) * 0.12;
  const along = softGust(gust * 0.7 + swell * 0.38 + eddy + plant);

  // Higher-frequency tip flutter; stronger while a gust is pushing.
  const flutter =
    Math.sin(tSec * 2.85 + acrossPos * 0.85 + x * 0.35) * 0.4 +
    Math.sin(tSec * 4.6 + alongPos * 0.4 + z * 0.5) * 0.25 +
    Math.sin(tSec * 1.3 + x * 1.1 - z * 0.7) * 0.15;
  const cross = flutter * (0.28 + Math.abs(along) * 0.45);

  return { along, cross };
}

/** Tip lean with the park wind front (downwind + soft cross flutter). */
function applyVegSway(dummy: THREE.Object3D, pose: VegPose, tSec: number, amp: number): void {
  const wind = parkWindSample(pose.x, pose.z, tSec);
  const along = wind.along * amp;
  const cross = wind.cross * amp;
  const rotX = along * PARK_WIND_DIR_Z + cross * PARK_WIND_DIR_X;
  const rotZ = -along * PARK_WIND_DIR_X + cross * PARK_WIND_DIR_Z;
  dummy.position.set(pose.x, pose.y, pose.z);
  dummy.rotation.set(rotX, pose.rotY, rotZ);
  dummy.scale.setScalar(pose.scale);
}

/** Soft feathered ovals under vegetation - shared contact-shadow texture, no shadow maps. */
function addGroundShadows(
  root: THREE.Group,
  poses: VegPose[],
  radiusX: number,
  radiusZ: number,
  opacity: number,
): void {
  const n = poses.length;
  if (n === 0) {
    return;
  }
  const mesh = new THREE.InstancedMesh(
    contactShadowGeometry(),
    contactShadowMaterial(opacity),
    n,
  );
  mesh.frustumCulled = true;
  mesh.renderOrder = -1;
  const dummy = new THREE.Object3D();
  for (let i = 0; i < n; i++) {
    const pose = poses[i]!;
    const s = Math.max(0.35, pose.scale);
    dummy.position.set(pose.x, pose.y + 0.025, pose.z);
    dummy.rotation.set(0, pose.rotY * 0.15, 0);
    dummy.scale.set(radiusX * s, 1, radiusZ * s);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  root.add(mesh);
}

type ColorRole = "leaf" | "trunk" | "other";

function colorRole(hex: number): ColorRole {
  const c = new THREE.Color(hex);
  // Lime / teal foliage.
  if (c.g > c.r + 0.04 && c.g > c.b * 0.75) {
    return "leaf";
  }
  // Warm bark / wood.
  if (c.r > c.g && c.r >= c.b - 0.05 && c.g > 0.22 && c.g < 0.78) {
    return "trunk";
  }
  return "other";
}

/** Approx normal via Box-Muller; seeded by world position so trees stay stable. */
function gauss01(x: number, z: number, salt: number): number {
  const u1 = Math.max(1e-4, hash01(x, z, salt));
  const u2 = hash01(z, x, salt + 19);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(Math.PI * 2 * u2);
}

function hash01(x: number, z: number, salt: number): number {
  const n = Math.sin(x * 12.9898 + z * 78.233 + salt * 45.164) * 43758.5453;
  return n - Math.floor(n);
}

function jitterInstanceColor(
  mesh: THREE.InstancedMesh,
  index: number,
  pose: VegPose,
  role: ColorRole,
  out: THREE.Color,
): void {
  // σ ≈ 6-8% brightness + small channel drift for leaf/trunk variety.
  const sigma = role === "leaf" ? 0.07 : 0.055;
  const brightness = 1 + gauss01(pose.x, pose.z, role === "leaf" ? 1 : 2) * sigma;
  const dr = gauss01(pose.x, pose.z, role === "leaf" ? 3 : 4) * (role === "leaf" ? 0.045 : 0.035);
  const dg = gauss01(pose.x, pose.z, role === "leaf" ? 5 : 6) * (role === "leaf" ? 0.05 : 0.03);
  const db = gauss01(pose.x, pose.z, role === "leaf" ? 7 : 8) * (role === "leaf" ? 0.04 : 0.025);
  out.setRGB(
    clamp01(brightness + dr),
    clamp01(brightness + dg),
    clamp01(brightness + db),
  );
  if (pose.autumn && role === "leaf" && pose.family !== "pine") {
    applyAutumnLeafTint(out, pose.x, pose.z);
  }
  mesh.setColorAt(index, out);
}

const AUTUMN_LEAF = [0xd4a84a, 0xc87830, 0xb86b3a, 0xa85828, 0xe8b040] as const;
const AUTUMN_TINT = new THREE.Color();

function applyAutumnLeafTint(out: THREE.Color, x: number, z: number): void {
  const pick = hash01(x, z, 41);
  const idx = Math.min(AUTUMN_LEAF.length - 1, Math.floor(pick * AUTUMN_LEAF.length));
  AUTUMN_TINT.setHex(AUTUMN_LEAF[idx]!);
  const mix = 0.68 + hash01(x, z, 42) * 0.24;
  out.lerp(AUTUMN_TINT, mix);
  const bright = 0.9 + hash01(x, z, 43) * 0.14;
  out.multiplyScalar(bright);
}

function clamp01(v: number): number {
  return Math.max(0.55, Math.min(1.35, v));
}

function writeInstance(
  dummy: THREE.Object3D,
  local: THREE.Object3D,
  mesh: THREE.InstancedMesh,
  index: number,
  pose: VegPose,
  lx: number,
  ly: number,
  lz: number,
  sx: number,
  sy: number,
  sz: number,
): void {
  dummy.position.set(pose.x, pose.y, pose.z);
  dummy.rotation.set(0, pose.rotY, 0);
  dummy.scale.setScalar(pose.scale);
  dummy.updateMatrix();
  local.position.set(lx, ly, lz);
  local.rotation.set(0, 0, 0);
  local.scale.set(sx, sy, sz);
  local.updateMatrix();
  dummy.matrix.multiply(local.matrix);
  mesh.setMatrixAt(index, dummy.matrix);
}

/** Convenience: park-prop scale with jitter. */
export function vegScale(base: number, jitter: number): number {
  return (base + jitter) * WORLD_PROP_SCALE;
}
